import { IEnergyEmission } from '../../hooks/api/energy-emissions';
import {
  ActionStatus,
  IInputMaterial,
  IMachine,
  IWasteManagement,
} from '../../pages/product-details/components/product-tab-view/tabs/pcf-tab/components/pcf-wizard/PcfWizard.type';
import { IdentifierType } from '../types/CompanyIdentifier';
import { DeclaredUnit } from './IComponentDetails';

/**
 * This is a enum that represents the foot print status that are supported
 */
export enum FootPrintStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  DEPRECATED = 'deprecated',
  EXPIRED = 'expired',
}

export enum PartialFullPcf {
  CRADLE_TO_GATE = 'Cradle-to-gate',
  CRADLE_TO_GRAVE = 'Cradle-to-grave',
}

export enum PFStatus {
  ACTIVE = 'Active',
  DEPRECATED = 'Deprecated',
}

export enum MainRoute {
  ROAD = 'ROAD',
  RAIL = 'RAIL',
  AIR = 'AIR',
  SEA = 'SEA',
  INLANDWATERS = 'INLANDWATERS',
}

export type PrecedingPfId = {
  id: string;
};

/**
 * Strictly positive decimals (e.g., > 0).
 */
export type StrictlyPositiveDecimal = number;

/**
 * Positive decimal weight (e.g., > 0).
 */
export type PositiveDecimalWeight = number;

/**
 * Emissions percentage (e.g., 0 to 100).
 */
export type ExemptedEmissionsPercent = number;

/**
 * Generic text type for descriptive fields.
 */
export type Text = string;

/**
 * Geography Country Subdivision (e.g., state, province).
 */
export type GeographyCountrySubdivision = string;

/**
 * Geography country type.
 */
export type GeographyCountry = string;

/**
 * Characteristic for a region or subregion.
 */
export type GeographyRegionOrSubregionCharacteristic = string;

/**
 * Set of cross-sectoral standards used in the carbon footprint calculation.
 */
export type CrossSectoralStandardSet = string[];

/**
 * Characterization factors used in carbon footprint calculations.
 */
export enum CharacterizationFactorsCharacteristic {
  AR5 = 'AR5',
  AR6 = 'AR6',
  AR4 = 'AR4',
  UNSPECIFIED = 'unspecified',
}

/**
 * Allocation rule for waste incineration emissions.
 */
export enum AllocationWasteIncinerationCharacteristic {
  CUT_OFF = 'cut-off',
  REVERSE_CUT_OFF = 'reverse cut-off',
  SYSTEM_EXPANSION = 'system expansion',
}

export const AllocationWasteIncinerationCharacteristicToLabelMap = {
  [AllocationWasteIncinerationCharacteristic.CUT_OFF]: 'Cut-off',
  [AllocationWasteIncinerationCharacteristic.REVERSE_CUT_OFF]: 'Reverse cut-off',
  [AllocationWasteIncinerationCharacteristic.SYSTEM_EXPANSION]: 'System expansion',
};

/**
 * Percentage value (0-100).
 */
export type Percent = number;

/**
 * Set of sources for emission factors used in carbon footprint calculations.
 */
export type EmissionFactorDSSet = {
  secondary_emission_factor_source: Text;
};

/**
 * Constraint for defining a decimal between 1 and 3 including.
 */
export type DqiNumberTrait = number;

/**
 * Indicators representing the data quality (e.g., low, medium, high).
 */
export type DataQualityIndicators = {
  coverage_percent: Percent;
  technological_DQR: DqiNumberTrait;
  temporal_DQR: DqiNumberTrait;
  geographical_DQR: DqiNumberTrait;
  completeness_DQR: DqiNumberTrait;
  reliability_DQR: DqiNumberTrait;
};

/**
 * Boolean characteristic indicating whether packaging emissions are included.
 */
export type BooleanCharacteristic = boolean;

/**
 * Only positive emission values (>0) are valid
 */
export type PositiveEmissions = number;

/**
 * Characteristic for defining (positive or negative) emissions in context of a PCF (Product Carbon Footprint)
 */
export type PositiveOrNegativeEmission = number;

/**
 * Only negative emission values (<=0) are valid.
 */
export type NegativeEmissions = number;

export type GeographyRegionOrSubregion = string;

// Suggested dropdown choices only - the backend field is free text, not a closed enum.
export enum CrossSectoralStandardsUsed {
  ISO_STANDARD_14067 = 'ISO 14067',
  ISO_STANDARD_14040_44 = 'ISO 14040-44',
  GHGP_PRODUCT = 'GHGP Product',
  PACT_V2 = 'PACT-2.0',
  PACT_V3 = 'PACT-3.0',
  PEF = 'PEF',
  OTHER = 'Other',
}

export enum CREATION_METHOD {
  MANUAL = 'manual',
  CALCULATION = 'calculation',
}

export enum RetroOrProspectivePcfType {
  RETROSPECTIVE = 'Retrospective PCF',
  PROSPECTIVE_WITHOUT_FORERUNNER = 'Prospective PCF without forerunner',
  PROSPECTIVE_WITH_FORERUNNER = 'Prospective PCF of further developed product with forerunner',
  PROSPECTIVE_FOR_FUTURE_PRODUCTION_DATE = 'Prospective PCF for current product for future production date',
}

export type PCFEntity = {
  declared_unit: DeclaredUnit;
  unitary_product_amount: StrictlyPositiveDecimal;
  product_mass_per_declared_unit?: number | null;
  retro_or_prospective_pcf_type?: RetroOrProspectivePcfType;
  exempted_emissions_percent: ExemptedEmissionsPercent;
  exempted_emissions_description?: Text;
  boundary_processes_description?: Text;
  geography_country_subdivision?: GeographyCountrySubdivision;
  geography_country?: GeographyCountry;
  geography_region_or_subregion: GeographyRegionOrSubregion;
  reference_period_start: string;
  reference_period_end: string;
  cross_sectoral_standards_used: string[];
  product_or_sector_specific_rules: string[];
  characterization_factors: CharacterizationFactorsCharacteristic;
  allocation_rules_description?: Text;
  allocation_waste_incineration: AllocationWasteIncinerationCharacteristic;
  primary_data_share?: Percent;
  secondary_emission_factor_sources: EmissionFactorDSSet[];
  data_quality_rating?: DataQualityIndicators;
  packaging_emissions_included: BooleanCharacteristic;
  pcf_excluding_biogenic: PositiveEmissions;
  pcf_including_biogenic?: PositiveOrNegativeEmission | null;
  fossil_ghg_emissions?: PositiveEmissions | null;
  biogenic_carbon_emissions_other_than_co2?: PositiveEmissions;
  biogenic_carbon_withdrawal?: PositiveEmissions;
  dluc_ghg_emissions?: PositiveEmissions;
  lu_ghg_emissions?: PositiveOrNegativeEmission;
  aircraft_ghg_emissions?: PositiveEmissions;
  packaging_ghg_emissions?: PositiveEmissions;
  distribution_stage_pcf_excluding_biogenic?: PositiveEmissions;
  distribution_stage_pcf_including_biogenic?: PositiveOrNegativeEmission;
  distribution_stage_fossil_ghg_emissions?: PositiveEmissions;
  distribution_stage_biogenic_carbon_emissions_other_than_co2?: PositiveEmissions;
  distribution_stage_biogenic_carbon_withdrawal?: NegativeEmissions;
  distribution_stage_dluc_ghg_emissions?: PositiveEmissions;
  distribution_stage_lu_ghg_emissions?: PositiveOrNegativeEmission;
  carbon_content_total?: PositiveEmissions | null;
  fossil_carbon_content?: PositiveEmissions | null;
  carbon_content_biogenic?: PositiveEmissions | null;
  distribution_stage_aircraft_ghg_emissions?: PositiveEmissions;
  ccs_technological_co2_capture_included?: BooleanCharacteristic;
  distribution_stage_included?: BooleanCharacteristic;
  mass_balancing_used?: BooleanCharacteristic;
  free_attribution_in_mass_balancing?: BooleanCharacteristic | null;
  mass_balancing_certificate_scheme?: Text | null;
};

export type IFootPrintTableRow = {
  id: number;
  footprintId: string;
  version: number;
  status: FootPrintStatus;
  dateCreated: string;
  pcfExcludingBiogenic?: number;
  declaredUnit?: DeclaredUnit;
  unitaryProductAmount?: number;
  creationMethod?: CREATION_METHOD;
};

export enum CalculationStatus {
  DRAFT = 'draft',
  COMPLETE = 'complete',
}

export type ICalculationDetailsTableRow = {
  id: number;
  name?: string;
  dateCreated: string;
  pcfExcludingBiogenic?: number;
  pcfIncludingBiogenic?: number;
  declaredUnit?: DeclaredUnit;
  unitaryProductAmount?: number;
  status: CalculationStatus;
};

export type IOtherRequestData = {
  supplier: {
    supplier_name: string;
  };
};

export type ILocation = {
  name?: string;
  coordinates?: {
    longitude?: number;
    latitude?: number;
  };
};

export type ICalculationDetails = {
  id?: number;
  unitary_product_amount?: StrictlyPositiveDecimal;
  declared_unit?: DeclaredUnit;
  product_mass_per_declared_unit?: PositiveDecimalWeight;
  partial_full_pcf?: PartialFullPcf;
  retro_or_prospective_pcf_type?: RetroOrProspectivePcfType;
  reference_period_end?: string;
  reference_period_start?: string;
  validity_period_end?: string;
  use_of_energy?: IEnergyEmission;
  action?: ActionStatus;
  processes?: IProcessDetails[];
  pcf_legal_statement?: string;
};

export type ICalculationResponse = {
  id: number;
  name?: string;
  footprintId: number;
  data: Omit<ICalculationDetails, 'id'>;
  dateCreated: Date;
  dateUpdated: Date;
  pcfIncludingBiogenic: number;
  pcfExcludingBiogenic: number;
  status: CalculationStatus;
  restricted?: boolean;
};

export type ITransportCalculation = {
  total_emissions?: number;
  distance?: number;
};

export type IProcessDetails = {
  name: string;
  process_id: string;
  order: number;
  route_name?: string;
  starting_point?: ILocation;
  end_location?: ILocation;
  main_route?: MainRoute;
  route_calculation?: ITransportCalculation;
  product_weight?: number;
  machines?:
    | (Omit<IMachine, 'manufacturer_id'> & {
        manufacturer?: {
          id: number;
          name: string;
        };
      })[]
    | undefined;
  input_materials?: IInputMaterial[];
  waste_management?: IWasteManagement | undefined;
  total_machine_emissions?: number;
};

export type IFootPrintDetails = {
  id: number;
  footprint_id: string;
  product_id: string;
  status: FootPrintStatus;
  spec_version: string;
  partial_full_pcf: PartialFullPcf;
  preceding_pf_ids?: PrecedingPfId[];
  version: number;
  date_created: string;
  pf_status: PFStatus;
  validity_period_start?: string;
  validity_period_end?: string;
  comment?: string;
  company_name: string;
  company_ids: string[];
  product_description?: Text;
  product_ids: string[];
  product_code_cpc: string;
  product_name: string;
  pcf: Partial<PCFEntity>;
  pcf_legal_statement?: string;
  date_published?: string;
  sender_company_name?: string;
  otherRequestData?: IOtherRequestData;
  company_identifiers?: IdentifierType[];
  creation_method?: CREATION_METHOD;
  s3PathPdf?: string;
  publishedCalculationId?: number;
};

export enum EmissionSymbol {
  KG_C = 'kg C',
  KG_CO2E = 'kg CO₂e',
  G_CO2E = 'g CO₂e',
}

export const packagingEmissionValueToLabelKey = (value: string): string => {
  switch (value) {
    case 'false':
      return 'global.excluded';
    case 'true':
      return 'global.included';
    default:
      return 'global.notSpecified';
  }
};
